'use client'

import { Skeleton } from '@brew/ui/skeleton'
import { useTranslations } from 'next-intl'
import {
  IntegrationDetailFrame,
  type IntegrationSurface,
} from './integration-detail-frame'

/**
 * Detail-page shell for `/integrations/[provider]` and its public mirror.
 * Must not reuse the catalog grid — that layout is a different shape and
 * causes a visible jump on Instant Nav.
 *
 * The frame comes from `IntegrationDetailFrame`, which the real page also
 * renders, so the column width and page padding cannot drift. Pass the same
 * `surface` the route will: the two differ in width, not just padding.
 *
 * A client component so its status label reads in the visitor's language
 * from the static shell: the app shell's catalogs in the app, the public
 * layout's (`browse/integrations/layout.tsx`) on the mirror.
 */
export function IntegrationDetailSkeleton({
  surface = 'app',
}: {
  surface?: IntegrationSurface
}) {
  const t = useTranslations('integrations')
  return (
    <IntegrationDetailFrame
      surface={surface}
      role="status"
      aria-busy
      aria-label={t('loading')}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
        <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-5">
          <Skeleton className="size-14 shrink-0 rounded-lg" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-7 w-40" />
            <Skeleton className="h-4 w-72 max-w-full" />
          </div>
        </div>
        <Skeleton className="h-9 w-28 rounded-lg" />
      </div>
      {/* The same columns as `BodyGrid`: by the page's own width, the rail
          on the left of two columns and below the tabs in one. */}
      <div className="@container mt-8 border-border border-t pt-8">
        <div className="grid @4xl:grid-cols-[200px_1fr] @4xl:gap-14 gap-10">
          <div className="@4xl:col-start-2 @4xl:row-start-1 flex min-w-0 flex-col gap-4">
            <Skeleton className="h-9 w-full max-w-md rounded-lg" />
            <Skeleton className="h-40 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
          </div>
          <div className="@4xl:col-start-1 @4xl:row-start-1 flex flex-col gap-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>
        </div>
      </div>
    </IntegrationDetailFrame>
  )
}
